import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Bug, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useChatStore, useSidebarStore, useSupportChatStore } from '../lib/stores';
import { useAuthStore, useCan, SETTINGS_ACCESS_PERMS, INSIGHTS_ACCESS_PERMS } from '../lib/authStore';
import { useLogout } from '../lib/useLogout';
import { deriveInitials } from '../lib/initials';
import { deleteConversation, getAiModels, getAppErrors, getConversations, getDashboard, getProfile, getSettings } from '../lib/api';
import { NewChatModal } from './NewChatModal';
import { Avatar } from './Avatar';
import { ConnectionStatus } from './ConnectionStatus';
import { Icon } from '../lib/icons';
import { APP_VERSION_LABEL } from '../lib/version';
import { useMediaQuery } from '../lib/useMediaQuery';

interface ContextMenuState {
  x: number;
  y: number;
  sessionId: string;
}

const FALLBACK_MODELS: Record<string, string[]> = {
  openai: ['gpt-4o'],
  ollama: ['llama3.1'],
};

/** Thin rule separating the nav groups (work / insights / reference). */
function NavDivider() {
  return (
    <div
      aria-hidden
      style={{ margin: 'var(--space-2) var(--space-2)', borderBottom: '1px solid var(--border-subtle)' }}
    />
  );
}

/** Count of items still "new" (unreviewed) for a nav item. Hidden when zero.
 *  Styled via `.sidebar-count-badge` (not inline) so the collapsed rail can
 *  float it onto the icon's top-right corner. */
function NavCountBadge({ count }: { count: number | undefined }) {
  if (!count) return null;
  return (
    <span className="sidebar-count-badge" aria-label={`${count} new`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Tracks whether a scroll container has more content above / below the fold, so
 *  the middle nav zone can fade the edge that continues. Re-measures on scroll,
 *  on resize of the box, and whenever rows are added or removed (conversations
 *  loading, permission-gated links appearing). */
function useScrollEdges<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const scrollable = el.scrollHeight - el.clientHeight > 1;
      const top = scrollable && el.scrollTop > 1;
      const bottom = scrollable && el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      // Bail on an unchanged reading — this runs on every scroll frame.
      setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    // childList only: our own re-render flips attributes, which would otherwise
    // re-trigger this observer on every measurement.
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', measure);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { ref, edges };
}

export function Sidebar() {
  const { sessions, setSessions, activeSessionId, setActiveSessionId, removeSession } = useChatStore();
  const { mobileOpen, setMobileOpen, collapsed, toggle } = useSidebarStore();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showAnalysisExpanded, setShowAnalysisExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Desktop-only collapse: 769px is the first px NOT covered by the mobile
  // `@media (max-width: 768px)` block, so the two states never overlap. The
  // persisted `collapsed` only takes visual effect on desktop — on the mobile
  // drawer we never apply the `.collapsed` class (which would shrink it to 72px).
  const isDesktop = useMediaQuery('(min-width: 769px)');
  const isCollapsed = collapsed && isDesktop;
  // The brand and the footer are pinned; only this middle zone scrolls.
  const { ref: scrollRef, edges: scrollEdges } = useScrollEdges<HTMLDivElement>();
  // What the zone was last scrolled to reveal — see the reveal effect below.
  const lastRevealed = useRef<{ row: Element; perms: string } | null>(null);

  const { data: modelsData } = useQuery({
    queryKey: ['ai-models'],
    queryFn: getAiModels,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Unreviewed ("new") counts per item type, for the nav badges.
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const newCounts = dashboard?.newItems;

  // Signed-in user, for the footer chip. Profile (name/title/initials) overrides
  // the auth-store basics once loaded; invalidating ['profile'] on save updates it.
  const authStatus = useAuthStore((s) => s.status);
  const authUser = useAuthStore((s) => s.user);
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    enabled: authStatus === 'authenticated',
    staleTime: 5 * 60 * 1000,
  });
  const logout = useLogout();

  // Keep the auth store's effective permissions fresh from the profile (so a
  // role edit is picked up live on the next profile fetch, not the next login).
  const setUserPermissions = useAuthStore((s) => s.setUserPermissions);
  useEffect(() => {
    if (profile?.permissions) {
      setUserPermissions(profile.permissions, profile.roles?.map((r) => r.name));
    }
  }, [profile, setUserPermissions]);

  // Settings is all actions — hide it entirely from users who can't use any of it.
  const canDo = useCan();
  const canAccessSettings = canDo(SETTINGS_ACCESS_PERMS);
  // Conversations area (new chat + panel + Discuss) is gated behind one permission.
  const canUseConversations = canDo('conversations.use');
  // Insights (any tab) and the Advanced menu are each permission-gated.
  const canViewInsights = canDo(INSIGHTS_ACCESS_PERMS);
  const canViewAdvanced = canDo('advanced.view');
  const canViewDiagnostics = canDo('diagnostics.view');
  // User Guides authoring (the WalkMe-replacement Designer) is permission-gated.
  const canAuthorGuides = canDo('guides.author');
  // Status Page admin console — either publishing incidents or configuring the
  // page gets you in (the screens gate their own actions more finely).
  const canManageStatus = canDo(['status.publish', 'status.manage']);
  // The gated nav links mount later, when the profile resolves, and shift every row
  // below them down — so this is part of the "reveal the active row" latch further
  // down. Advanced/Diagnostics are excluded: they sit in the trailing icon row and
  // move nothing above themselves.
  const navPermsKey = [canViewInsights, canManageStatus, canAuthorGuides, canAccessSettings, canUseConversations].join('|');
  // Live count of OPEN errors for the Diagnostics badge. The 'errors' realtime
  // entity (RealtimeBridge) invalidates ['app-errors'] on every server push, so
  // this prefix-matched query auto-refetches the moment an error is recorded or
  // resolved — no polling. pageSize:1 → we only read `total`.
  const { data: openErrorCount = 0 } = useQuery({
    queryKey: ['app-errors', 'open-count'],
    queryFn: () => getAppErrors({ resolved: false, pageSize: 1 }),
    select: (d) => d.total,
    enabled: canViewDiagnostics,
    staleTime: 30_000,
  });

  // Support chat now lives as an icon in the action row (replacing the floating
  // corner bubble). Respect the master enable switch + carry the unread badge.
  const openSupport = useSupportChatStore((s) => s.openWidget);
  const supportUnread = useSupportChatStore((s) => s.unread);
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 60_000 });
  const supportEnabled = (settings as any)?.support?.enabled !== false;

  const profileName = profile?.displayName || authUser?.displayName || authUser?.email || 'Account';
  const profileSub = profile?.jobTitle || profile?.email || authUser?.email || '';
  const profileInitials =
    (profile?.initials?.trim().toUpperCase()) ||
    deriveInitials(profile?.displayName || authUser?.displayName || '', profile?.firstName || '', profile?.lastName || '');
  const showUser = authStatus === 'authenticated';

  const availableModels: Record<string, string[]> = {
    openai: modelsData?.openai?.length ? modelsData.openai : FALLBACK_MODELS.openai,
    ollama: modelsData?.ollama?.length ? modelsData.ollama : FALLBACK_MODELS.ollama,
  };

  // Load conversations on mount (skip entirely when the user can't use them).
  useEffect(() => {
    if (!canUseConversations) return;
    getConversations()
      .then(setSessions)
      .catch((err) => console.error('Failed to load sessions', err));
  }, [setSessions, canUseConversations]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // On a cold load of a deep page, the active link can sit below the fold of the
  // scrolling zone. Nudge it into view — adjusting the zone's own scrollTop only,
  // never scrollIntoView(), which would also scroll the page behind the rail.
  useEffect(() => {
    const el = scrollRef.current;
    const active = el?.querySelector<HTMLElement>('.sidebar-link.active');
    // No layout box → a row inside a display:none subtree (the collapsed rail
    // hides the whole conversations block). It measures as 0x0 at (0,0), and
    // scrolling off that rect is scrolling off garbage.
    if (!el || !active || !active.getClientRects().length) return;
    // Reveal once per (row, nav shape). A prefix NavLink stays active across its
    // detail pages (/backlog → /backlog/1234), and re-running there would yank the
    // zone back to the nav from wherever the user had scrolled it. The perms part
    // still re-runs it when the gated links mount and push the active row down.
    const seen = lastRevealed.current;
    if (seen && seen.row === active && seen.perms === navPermsKey) return;
    lastRevealed.current = { row: active, perms: navPermsKey };
    const box = el.getBoundingClientRect();
    const row = active.getBoundingClientRect();
    const margin = 8;
    if (row.bottom > box.bottom - margin) el.scrollTop += row.bottom - box.bottom + margin;
    else if (row.top < box.top + margin) el.scrollTop -= box.top + margin - row.top;
  }, [location.pathname, scrollRef, navPermsKey]);

  const handleContextMenu = (e: React.MouseEvent, sessionId: string) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      sessionId,
    });
  };

  const handleDelete = async (sessionId: string) => {
    setContextMenu(null);
    try {
      await deleteConversation(sessionId);
      removeSession(sessionId);
    } catch (err) {
      console.error('Failed to delete conversation', err);
      alert('Failed to delete conversation');
    }
  };

  const handleSelectConversation = (sessionId: string) => {
    setActiveSessionId(sessionId);
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
    setMobileOpen(false);
  };

  const handleNewChat = () => {
    setShowNewChatModal(true);
  };

  const handleNewChatClose = () => {
    setShowNewChatModal(false);
    // Navigate to chat if not already there
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
    setMobileOpen(false);
  };

  return (
    <>
      <aside
        className={`app-sidebar glass-sidebar ${isCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
      >
        {/* Three zones: static brand, scrolling nav, static footer. The layout
            (incl. the collapsed rail's tighter padding) is all in `.sidebar` — no
            inline padding, so the mobile drawer's safe-area inset can apply. */}
        <div className="sidebar">
          {/* Brand — static top */}
          <div className="sidebar-brand">
            <img className="sidebar-logo" src="/logo.svg" alt="Personal Control Plane" width={36} height={36} />
            <div className="sidebar-brand-text">
              <span className="sidebar-brand-name">Personal Control Plane</span>
              <span className="sidebar-brand-tagline" title="Build running on this environment">{APP_VERSION_LABEL}</span>
            </div>
          </div>

          {/* Scrolling middle zone — everything between the brand and the footer.
              `data-fade-*` drives the edge fades that signal there is more here. */}
          <div
            ref={scrollRef}
            className="sidebar-scroll"
            data-fade-top={scrollEdges.top ? '' : undefined}
            data-fade-bottom={scrollEdges.bottom ? '' : undefined}
          >
          {/* Navigation Links */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <NavLink
              to="/"
              end
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Dashboard"
              aria-label="Dashboard"
            >
              <span className="sidebar-link-icon"><Icon name="dashboard" /></span>
              <span className="sidebar-link-text">Dashboard</span>
            </NavLink>
            <NavLink
              to="/tenants"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Tenants"
              aria-label="Tenants"
            >
              <span className="sidebar-link-icon"><Icon name="tenants" /></span>
              <span className="sidebar-link-text">Tenants</span>
            </NavLink>
            <NavLink
              to="/tickets"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Tickets"
              aria-label="Tickets"
            >
              <span className="sidebar-link-icon"><Icon name="tickets" /></span>
              <span className="sidebar-link-text">Tickets</span>
              <NavCountBadge count={newCounts?.zoho.count} />
            </NavLink>
            <NavLink
              to="/topics"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Topics"
              aria-label="Topics"
            >
              <span className="sidebar-link-icon"><Icon name="topics" /></span>
              <span className="sidebar-link-text">Topics</span>
              <NavCountBadge count={newCounts?.discourse.count} />
            </NavLink>
            <NavLink
              to="/backlog"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Backlog"
              aria-label="Backlog"
            >
              <span className="sidebar-link-icon"><Icon name="backlog" /></span>
              <span className="sidebar-link-text">Backlog</span>
              <NavCountBadge count={newCounts?.azureDevops.count} />
            </NavLink>
            <NavLink
              to="/companies"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Companies"
              aria-label="Companies"
            >
              <span className="sidebar-link-icon"><Icon name="companies" /></span>
              <span className="sidebar-link-text">Companies</span>
            </NavLink>
            <NavLink
              to="/inbox"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Inbox"
              aria-label="Inbox"
            >
              <span className="sidebar-link-icon"><Icon name="inbox" /></span>
              <span className="sidebar-link-text">Inbox</span>
            </NavLink>
            <NavLink
              to="/commits"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Commit Tracker"
              aria-label="Commit Tracker"
            >
              <span className="sidebar-link-icon"><Icon name="commits" /></span>
              <span className="sidebar-link-text">Commit Tracker</span>
              <NavCountBadge count={newCounts?.commits.count} />
            </NavLink>
            <NavLink
              to="/releases"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Release Planning"
              aria-label="Release Planning"
            >
              <span className="sidebar-link-icon"><Icon name="releases" /></span>
              <span className="sidebar-link-text">Release Planning</span>
            </NavLink>
            <NavLink
              to="/support"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="AI Support"
              aria-label="AI Support"
            >
              <span className="sidebar-link-icon"><Icon name="support" /></span>
              <span className="sidebar-link-text">AI Support</span>
            </NavLink>
            {canManageStatus && (
              <NavLink
                to="/status"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
                title="Status Page"
                aria-label="Status Page"
              >
                <span className="sidebar-link-icon"><Icon name="statusPage" /></span>
                <span className="sidebar-link-text">Status Page</span>
              </NavLink>
            )}
            {canViewInsights && (<>
              <NavDivider />
              <NavLink
                to="/insights"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
                title="Insights"
                aria-label="Insights"
              >
                <span className="sidebar-link-icon"><Icon name="insights" /></span>
                <span className="sidebar-link-text">Insights</span>
              </NavLink>
            </>)}
            <NavDivider />
            <NavLink
              to="/knowledge"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Knowledge Base"
              aria-label="Knowledge Base"
            >
              <span className="sidebar-link-icon"><Icon name="documentation" /></span>
              <span className="sidebar-link-text">Knowledge Base</span>
            </NavLink>
            <NavLink
              to="/solution-design"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Solution Design"
              aria-label="Solution Design"
            >
              <span className="sidebar-link-icon"><Icon name="design" /></span>
              <span className="sidebar-link-text">Solution Design</span>
            </NavLink>
            <NavLink
              to="/playbooks"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Playbooks"
              aria-label="Playbooks"
            >
              <span className="sidebar-link-icon"><Icon name="playbook" /></span>
              <span className="sidebar-link-text">Playbooks</span>
            </NavLink>
            <NavLink
              to="/entitlements"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="Entitlements"
              aria-label="Entitlements"
            >
              <span className="sidebar-link-icon"><Icon name="entitlements" /></span>
              <span className="sidebar-link-text">Entitlements</span>
            </NavLink>
            <NavLink
              to="/coco-config"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title="CoCo Config"
              aria-label="CoCo Config"
            >
              <span className="sidebar-link-icon"><Icon name="folder" /></span>
              <span className="sidebar-link-text">CoCo Config</span>
            </NavLink>
            {canAuthorGuides && (
              <NavLink
                to="/guides"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
                title="Smartguides"
                aria-label="Smartguides"
              >
                <span className="sidebar-link-icon"><Icon name="guides" /></span>
                <span className="sidebar-link-text">Smartguides</span>
              </NavLink>
            )}
            {/* Tight action row — Advanced, Settings and support chat collapse to
                icon-only targets so the rail stays compact and the support
                launcher no longer floats over (and blocks) the corner. */}
            <div className="sidebar-action-row">
              {canViewAdvanced && (
                <NavLink
                  to="/advanced"
                  className={({ isActive }) => `sidebar-icon-link ${isActive ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                  title="Advanced"
                  aria-label="Advanced"
                >
                  <Icon name="tools" />
                </NavLink>
              )}
              {canViewDiagnostics && (
                <NavLink
                  to="/diagnostics"
                  className={({ isActive }) => `sidebar-icon-link ${isActive ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                  title={openErrorCount > 0 ? `Diagnostics — ${openErrorCount} open error${openErrorCount === 1 ? '' : 's'}` : 'Diagnostics'}
                  aria-label={openErrorCount > 0 ? `Diagnostics — ${openErrorCount} open errors` : 'Diagnostics'}
                >
                  <Bug size={18} />
                  {openErrorCount > 0 && (
                    <span
                      className="sidebar-icon-badge"
                      style={{ background: 'var(--accent-error)', color: 'var(--text-inverse)' }}
                      aria-label={`${openErrorCount} open errors`}
                    >
                      {openErrorCount > 99 ? '99+' : openErrorCount}
                    </span>
                  )}
                </NavLink>
              )}
              {canAccessSettings && (
                <NavLink
                  to="/settings"
                  className={({ isActive }) => `sidebar-icon-link ${isActive ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                  title="Settings"
                  aria-label="Settings"
                >
                  <Icon name="settings" />
                </NavLink>
              )}
              {supportEnabled && (
                <button
                  type="button"
                  className="sidebar-icon-link"
                  onClick={() => { setMobileOpen(false); void openSupport(); }}
                  title="Chat with support"
                  aria-label="Chat with support"
                >
                  <Icon name="support" />
                  {supportUnread > 0 && (
                    <span className="sidebar-icon-badge" aria-label={`${supportUnread} unread`}>
                      {supportUnread > 9 ? '9+' : supportUnread}
                    </span>
                  )}
                </button>
              )}
            </div>
          </nav>

          {/* Conversations area — hidden unless the user holds conversations.use.
              No spacer needed when hidden: the scrolling zone takes the slack and
              the footer stays pinned to the bottom either way. */}
          {canUseConversations && (<>
          {/* Divider */}
          <div className="sidebar-conversations-divider" style={{
            margin: 'var(--space-3) 0',
            borderBottom: '1px solid var(--border-subtle)',
          }} />

          {/* New Conversation Button */}
          <button
            onClick={handleNewChat}
            className="btn btn-primary sidebar-new-chat"
            style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', paddingLeft: 'var(--space-3)' }}
          >
            <span style={{ marginRight: 'var(--space-2)', fontSize: '1.1em' }}>+</span> New Conversation
          </button>

          {/* Conversations List — scrolls with the rest of the middle zone rather
              than in a nested scroller of its own (one scrollbar, one gesture). */}
          <div className="sidebar-conversations" style={{ display: 'flex', flexDirection: 'column', marginTop: 'var(--space-3)' }}>
            <div className="sidebar-section-label">Conversations</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              {(() => {
                const regularSessions = sessions.filter((s) => !s.title?.startsWith('[Analysis]'));
                const analysisSessions = sessions.filter((s) => s.title?.startsWith('[Analysis]'));

                return (
                  <>
                    {regularSessions.length === 0 && analysisSessions.length === 0 && (
                      <div style={{
                        padding: 'var(--space-4) var(--space-2)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-tertiary)',
                        textAlign: 'center',
                      }}>
                        No conversations yet
                      </div>
                    )}
                    {regularSessions.map((session) => {
                      const isActive = session.id === activeSessionId && location.pathname === '/chat';
                      return (
                        <div
                          key={session.id}
                          className={`sidebar-link ${isActive ? 'active' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectConversation(session.id)}
                          onContextMenu={(e) => handleContextMenu(e, session.id)}
                        >
                          <span className="sidebar-link-icon"><Icon name="conversation" size={15} /></span>
                          <span
                            className="sidebar-link-text"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              opacity: isActive ? 1 : 0.7,
                            }}
                          >
                            {session.title || 'New Chat'}
                          </span>
                        </div>
                      );
                    })}
                    {analysisSessions.length > 0 && (
                      <>
                        <div
                          className="sidebar-link"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => setShowAnalysisExpanded((v) => !v)}
                        >
                          <span className="sidebar-link-icon" style={{ transition: 'transform 0.15s', transform: showAnalysisExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}><Icon name="chevronRight" size={13} /></span>
                          <span className="sidebar-link-text" style={{ opacity: 0.7 }}>
                            Analysis Logs
                          </span>
                          <span style={{
                            marginLeft: 'auto',
                            fontSize: '10px',
                            background: 'var(--accent-primary-soft)',
                            color: 'var(--accent-primary)',
                            padding: '1px 6px',
                            borderRadius: 'var(--radius-full)',
                            fontWeight: 600,
                          }}>
                            {analysisSessions.length}
                          </span>
                        </div>
                        {showAnalysisExpanded && analysisSessions.map((session) => {
                          const isActive = session.id === activeSessionId && location.pathname === '/chat';
                          const displayTitle = session.title?.replace('[Analysis] ', '') || 'Analysis';
                          return (
                            <div
                              key={session.id}
                              className={`sidebar-link ${isActive ? 'active' : ''}`}
                              style={{ cursor: 'pointer', paddingLeft: 'var(--space-5)' }}
                              onClick={() => handleSelectConversation(session.id)}
                              onContextMenu={(e) => handleContextMenu(e, session.id)}
                            >
                              <span className="sidebar-link-icon"><Icon name="analysis" size={13} /></span>
                              <span
                                className="sidebar-link-text"
                                style={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  opacity: isActive ? 1 : 0.6,
                                  fontSize: 'var(--text-xs)',
                                }}
                              >
                                {displayTitle}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          </>)}
          </div>
          {/* Footer — static bottom */}
          <div className="sidebar-footer">
            {showUser && (
              <div className="sidebar-user">
                <NavLink
                  to="/profile"
                  className={({ isActive }) => `sidebar-user-chip${isActive ? ' active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                  title="Your profile"
                >
                  <Avatar url={profile?.avatarUrl} initials={profileInitials} name={profileName} size={32} />
                  <span className="sidebar-user-meta">
                    <span className="sidebar-user-name">{profileName}</span>
                    {profileSub && <span className="sidebar-user-sub">{profileSub}</span>}
                  </span>
                </NavLink>
                <button
                  className="sidebar-user-logout"
                  onClick={() => void logout()}
                  title="Log out"
                  aria-label="Log out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}
            <div className="sidebar-status-row">
              <ConnectionStatus />
              {/* Desktop-only collapse toggle, pinned in the footer beside the
                  status row so it stays reachable while the nav scrolls. It uses
                  raw `collapsed` for its own icon/label and `isDesktop` to gate
                  rendering, so it never appears in the mobile drawer. */}
              {isDesktop && (
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={toggle}
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: 'var(--space-1)',
            zIndex: 1000,
            minWidth: '150px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            style={{
              width: '100%',
              textAlign: 'left',
              padding: 'var(--space-2) var(--space-3)',
              background: 'transparent',
              border: 'none',
              color: 'var(--accent-error)',
              cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--text-sm)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-error-soft)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => handleDelete(contextMenu.sessionId)}
          >
            Delete Conversation
          </button>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && canUseConversations && (
        <NewChatModal
          onClose={handleNewChatClose}
          availableModels={availableModels}
        />
      )}
    </>
  );
}
